// src/pages/Accounting/JournalEntries/ImportJournalEntryDialog.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography, IconButton, TextField, MenuItem,
  CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import { fieldSx, fmtMoney } from '../../../components/Reports/ReportKit';

const EMPTY_MAP = { accountCode: '', debit: '', credit: '', description: '' };
const BORDER = '#e5e7eb';
const toNum = (v) => {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, '').replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  return Number.isFinite(n) ? n : 0;
};

// تخمين الأعمدة من أسماء العناوين (عربي/إنجليزي)
function guessMapping(headers) {
  const g = { ...EMPTY_MAP };
  headers.forEach((h, i) => {
    const s = String(h ?? '').toLowerCase();
    if (!g.accountCode && /(code|account|رمز|رقم الحساب|حساب)/.test(s)) g.accountCode = String(i);
    else if (!g.debit && /(debit|debt|مدين)/.test(s)) g.debit = String(i);
    else if (!g.credit && /(credit|cred|دائن)/.test(s)) g.credit = String(i);
    else if (!g.description && /(desc|note|وصف|بيان|ملاحظ)/.test(s)) g.description = String(i);
  });
  return g;
}

export default function ImportJournalEntryDialog({ open, onClose, onImport }) {
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState(EMPTY_MAP);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // إعادة التهيئة عند كل فتح + تحميل الحسابات القابلة للترحيل
  useEffect(() => {
    if (!open) return;
    setFileName(''); setHeaders([]); setRows([]); setMapping(EMPTY_MAP);
    setLoadingAccounts(true);
    chartOfAccountsApi.getPostable()
      .then(res => setAccounts(res.data || []))
      .catch(err => { console.error('Error loading accounts', err); toast.error('تعذّر تحميل دليل الحسابات'); })
      .finally(() => setLoadingAccounts(false));
  }, [open]);

  const readFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
        if (!json.length) { toast.warning('الملف فارغ'); return; }
        const hdr = json[0].map(h => String(h ?? '').trim());
        setHeaders(hdr);
        setRows(json.slice(1));
        setMapping(guessMapping(hdr));
      } catch (err) {
        console.error(err);
        toast.error('تعذّر قراءة الملف — تأكد أنه Excel أو CSV');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const accountByCode = useMemo(() => {
    const m = new Map();
    accounts.forEach(a => m.set(String(a.code).trim(), a));
    return m;
  }, [accounts]);

  // معاينة النتيجة
  const preview = useMemo(() => {
    if (!headers.length || mapping.accountCode === '') return null;
    const ci = Number(mapping.accountCode);
    const di = mapping.debit === '' ? -1 : Number(mapping.debit);
    const ri = mapping.credit === '' ? -1 : Number(mapping.credit);
    const ti = mapping.description === '' ? -1 : Number(mapping.description);
    const out = [];
    rows.forEach((row, idx) => {
      const code = String(row[ci] ?? '').trim();
      if (!code) return;
      const debit = di > -1 ? toNum(row[di]) : 0;
      const credit = ri > -1 ? toNum(row[ri]) : 0;
      if (!debit && !credit) return;
      const acc = accountByCode.get(code);
      out.push({
        row: idx + 2, code, debit, credit,
        description: ti > -1 ? String(row[ti] ?? '') : '',
        account: acc || null,
      });
    });
    const ok = out.filter(r => r.account);
    const debit = ok.reduce((s, r) => s + r.debit, 0);
    const credit = ok.reduce((s, r) => s + r.credit, 0);
    return { all: out, ok, missing: out.filter(r => !r.account), debit, credit, balanced: Math.abs(debit - credit) < 0.01 };
  }, [headers, rows, mapping, accountByCode]);

  const canImport = preview && preview.ok.length > 0 && (mapping.debit !== '' || mapping.credit !== '') && !loadingAccounts;

  const handleImport = () => {
    if (!canImport) return;
    onImport(preview.ok.map(r => ({
      accountId: r.account.id,
      accountCode: r.account.code,
      accountName: r.account.nameAr,
      description: r.description,
      debit: r.debit,
      credit: r.credit,
    })));
    if (preview.missing.length) {
      toast.warning(`تم استيراد ${preview.ok.length} سطر — ${preview.missing.length} سطر بحساب غير موجود تم تجاهله`);
    } else {
      toast.success(`تم استيراد ${preview.ok.length} سطر بنجاح`);
    }
    onClose();
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['رمز الحساب', 'مدين', 'دائن', 'البيان'],
      ['1101', 1000, 0, 'رصيد افتتاحي - الصندوق'],
      ['3101', 0, 1000, 'رأس المال'],
    ]);
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    wb.Workbook = { Views: [{ RTL: true }] };
    XLSX.utils.book_append_sheet(wb, ws, 'القيد');
    XLSX.writeFile(wb, 'قالب-استيراد-قيد.xlsx');
  };

  const ColumnSelect = ({ field, label, required }) => (
    <TextField
      select size="small" fullWidth label={label} required={required}
      value={mapping[field]} onChange={e => setMapping(m => ({ ...m, [field]: e.target.value }))}
      sx={fieldSx}
    >
      <MenuItem value="">— بدون —</MenuItem>
      {headers.map((h, i) => <MenuItem key={i} value={String(i)}>{h || `عمود ${i + 1}`}</MenuItem>)}
    </TextField>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl"
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      {/* الترويسة */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, color: '#fff',
        background: 'linear-gradient(120deg, #4338ca 0%, #4f46e5 45%, #6366f1 100%)',
      }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.16)' }}>
          <UploadFileOutlinedIcon fontSize="small" />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 16, flex: 1 }}>استيراد سطور القيد من Excel</Typography>
        <Button size="small" onClick={handleDownloadTemplate} startIcon={<FileDownloadOutlinedIcon />}
          sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.12)', borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .6, mr: -.25 } }}>
          تحميل القالب
        </Button>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }} aria-label="إغلاق"><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 2, display: 'grid', gap: 1.5 }}>
        {/* اختيار الملف */}
        <Box
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
          sx={{
            border: `2px dashed ${dragOver ? '#4f46e5' : '#cbd5e1'}`, borderRadius: '14px', p: 2.5, textAlign: 'center',
            bgcolor: dragOver ? '#eef2ff' : '#f8fafc', cursor: 'pointer', transition: 'all .15s',
            '&:hover': { borderColor: '#4f46e5', bgcolor: '#eef2ff' },
          }}
        >
          <input ref={inputRef} type="file" hidden accept=".xlsx,.xls,.csv"
            onChange={e => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
          {fileName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <InsertDriveFileOutlinedIcon sx={{ color: '#059669' }} />
              <Typography sx={{ fontWeight: 700 }}>{fileName}</Typography>
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>· {rows.length} صف — اضغط لتغيير الملف</Typography>
            </Box>
          ) : (
            <>
              <UploadFileOutlinedIcon sx={{ fontSize: 36, color: '#94a3b8' }} />
              <Typography sx={{ fontWeight: 700, mt: .5 }}>اسحب ملف Excel هنا أو اضغط للاختيار</Typography>
              <Typography sx={{ fontSize: 13, color: '#64748b' }}>xlsx · xls · csv — الصف الأول عناوين الأعمدة</Typography>
            </>
          )}
        </Box>

        {/* تعيين الأعمدة */}
        {headers.length > 0 && (
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
            <ColumnSelect field="accountCode" label="رمز الحساب" required />
            <ColumnSelect field="debit" label="مدين" />
            <ColumnSelect field="credit" label="دائن" />
            <ColumnSelect field="description" label="البيان" />
          </Box>
        )}

        {/* المعاينة */}
        {preview && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                [`${preview.ok.length} سطر جاهز`, '#d1fae5', '#047857'],
                ...(preview.missing.length ? [[`${preview.missing.length} حساب غير موجود`, '#ffe4e6', '#be123c']] : []),
                [`مدين ${fmtMoney(preview.debit)}`, '#e0f2fe', '#0369a1'],
                [`دائن ${fmtMoney(preview.credit)}`, '#e0f2fe', '#0369a1'],
                [preview.balanced ? 'متوازن' : `غير متوازن (${fmtMoney(Math.abs(preview.debit - preview.credit))})`,
                  preview.balanced ? '#d1fae5' : '#fef3c7', preview.balanced ? '#047857' : '#b45309'],
              ].map(([l, bg, fg]) => (
                <Box key={l} sx={{ px: 1.25, py: .4, borderRadius: '999px', fontSize: 13, fontWeight: 700, bgcolor: bg, color: fg }}>{l}</Box>
              ))}
            </Box>

            {!preview.balanced && preview.ok.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: '12px', py: 0 }}>
                السطور غير متوازنة — يمكنك الاستيراد ثم استخدام زر "موازنة الفرق" في القيد.
              </Alert>
            )}

            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'auto', maxHeight: 280 }}>
              <Box component="table" sx={{
                width: '100%', borderCollapse: 'collapse', fontSize: 13,
                '& th': { position: 'sticky', top: 0, bgcolor: '#f8fafc', fontWeight: 800, textAlign: 'right', px: 1, py: .75, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' },
                '& td': { px: 1, py: .6, borderBottom: '1px solid #f1f5f9' },
              }}>
                <thead>
                  <tr><th>صف</th><th>الرمز</th><th>الحساب</th><th>مدين</th><th>دائن</th><th>البيان</th></tr>
                </thead>
                <tbody>
                  {preview.all.slice(0, 200).map(r => (
                    <tr key={r.row} style={{ background: r.account ? undefined : '#fff1f2' }}>
                      <td style={{ color: '#94a3b8' }}>{r.row}</td>
                      <td style={{ fontWeight: 700 }}>{r.code}</td>
                      <td>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, color: r.account ? 'inherit' : '#be123c' }}>
                          {r.account ? <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#059669' }} /> : <ErrorOutlineIcon sx={{ fontSize: 15 }} />}
                          {r.account ? r.account.nameAr : 'غير موجود في الدليل'}
                        </Box>
                      </td>
                      <td style={{ direction: 'ltr', textAlign: 'right' }}>{r.debit ? fmtMoney(r.debit) : ''}</td>
                      <td style={{ direction: 'ltr', textAlign: 'right' }}>{r.credit ? fmtMoney(r.credit) : ''}</td>
                      <td>{r.description}</td>
                    </tr>
                  ))}
                  {!preview.all.length && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748b', padding: 16 }}>لا توجد صفوف بمبالغ — راجع تعيين الأعمدة</td></tr>
                  )}
                </tbody>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, gap: 1, borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, borderRadius: '10px' }}>إلغاء</Button>
        <Button
          variant="contained" disableElevation onClick={handleImport} disabled={!canImport}
          startIcon={loadingAccounts ? <CircularProgress size={16} color="inherit" /> : <UploadFileOutlinedIcon />}
          sx={{ fontWeight: 800, borderRadius: '10px', minWidth: 140, bgcolor: '#4f46e5', '& .MuiButton-startIcon': { ml: .6, mr: -.25 } }}
        >
          {loadingAccounts ? 'تحميل الحسابات…' : preview?.ok.length ? `استيراد ${preview.ok.length} سطر` : 'استيراد'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
